'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useIsClient } from 'usehooks-ts';
import { AmplienceResponsiveImageData, AmplienceResponsiveImageProps } from '@/components/ResponsiveImage/AmplienceResponsiveImage';
import { getAmplienceImageSrcSet, getCssAspectRatio } from '@/components/ResponsiveImage/amplienceImageUtils';
import { getAmplienceImageUrl } from '@/utility/getImageUrl';
type LazyAmplienceResponsiveImageProps = Omit<AmplienceResponsiveImageProps, 'render'>;
export const LazyAmplienceResponsiveImage = t0 => {
  const $ = _c(47);
  let alt;
  let className;
  let loadingBehaviour;
  let priority;
  let rest;
  let source;
  if ($[0] !== t0) {
    ({
      source,
      loading: loadingBehaviour,
      className,
      alt,
      priority,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = className;
    $[3] = loadingBehaviour;
    $[4] = priority;
    $[5] = rest;
    $[6] = source;
  } else {
    alt = $[1];
    className = $[2];
    loadingBehaviour = $[3];
    priority = $[4];
    rest = $[5];
    source = $[6];
  }
  const ref = useRef(null);
  const isClient = useIsClient();
  const [loading, setLoading] = useState(true);
  let t1;
  if ($[7] !== source) {
    t1 = source instanceof Array ? undefined : getCssAspectRatio(source.aspectRatio);
    $[7] = source;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const cssAspectRatio = t1;
  let t2;
  bb0: {
    if (!isClient) {
      let t3;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {};
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      t2 = t3;
      break bb0;
    }
    let sources;
    let t3;
    if ($[10] !== source) {
      sources = source instanceof Array ? source : [source];
      t3 = sources.map(_temp).filter(_temp2);
      $[10] = source;
      $[11] = sources;
      $[12] = t3;
    } else {
      sources = $[11];
      t3 = $[12];
    }
    const sourceData = t3;
    let t4;
    if ($[13] !== sourceData || $[14] !== sources) {
      t4 = {
        sources,
        sourceData
      };
      $[13] = sourceData;
      $[14] = sources;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    t2 = t4;
  }
  const {
    sources: sources_0,
    sourceData: sourceData_0
  } = t2;
  let t3;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (ref.current && ref.current.complete) {
        setLoading(false);
      }
    };
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== sourceData_0 || $[18] !== sources_0) {
    t4 = [sources_0, sourceData_0];
    $[17] = sourceData_0;
    $[18] = sources_0;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  useEffect(t3, t4);
  const t5 = className ? className : "relative size-full object-cover";
  let t6;
  if ($[20] !== t5) {
    t6 = clsx(t5);
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== sourceData_0) {
    t7 = !!sourceData_0 && sourceData_0.length > 1 && sourceData_0.map(_temp3);
    $[22] = sourceData_0;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const t8 = sourceData_0?.[0]?.srcSet;
  const t9 = sources_0?.[0]?.sizes;
  const t10 = loadingBehaviour ?? "lazy";
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setLoading(false);
    };
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = className ? className : cssAspectRatio ? "h-auto w-full object-cover" : "size-full object-cover";
  let t13;
  if ($[25] !== t12) {
    t13 = clsx("text-transparent", t12);
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== cssAspectRatio || $[28] !== rest.style) {
    t14 = cssAspectRatio ? {
      aspectRatio: cssAspectRatio,
      ...rest.style
    } : rest.style;
    $[27] = cssAspectRatio;
    $[28] = rest.style;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== alt || $[31] !== loading || $[32] !== priority || $[33] !== rest || $[34] !== t10 || $[35] !== t13 || $[36] !== t14 || $[37] !== t8 || $[38] !== t9) {
    t15 = <img {...rest} ref={ref} aria-busy={loading} srcSet={t8} sizes={t9} alt={alt} loading={t10} onLoad={t11} fetchPriority={priority} className={t13} style={t14} />;
    $[30] = alt;
    $[31] = loading;
    $[32] = priority;
    $[33] = rest;
    $[34] = t10;
    $[35] = t13;
    $[36] = t14;
    $[37] = t8;
    $[38] = t9;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== loading) {
    t16 = loading && <span aria-hidden={true} className="absolute inset-0 size-full bg-[rgb(242,242,242)] before:absolute before:inset-0 before:size-full before:animate-pulse before:bg-gray-300" />;
    $[40] = loading;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t15 || $[43] !== t16 || $[44] !== t6 || $[45] !== t7) {
    t17 = <picture className={t6}>{t7}{t15}{t16}</picture>;
    $[42] = t15;
    $[43] = t16;
    $[44] = t6;
    $[45] = t7;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
};
function _temp(t0) {
  const {
    src,
    widths,
    sizes,
    media,
    ...options
  } = t0;
  if (!src) {
    console.warn("ResponsiveImage: No image provided");
    return null;
  }
  const imageUrl = getAmplienceImageUrl(src, {
    quality: options.quality,
    aspectRatio: options.aspectRatio
  });
  const srcSet = getAmplienceImageSrcSet(imageUrl, widths);
  return {
    srcSet,
    sizes,
    media
  };
}
function _temp2(data) {
  return data !== null;
}
function _temp3(data_0, index) {
  return <source {...data_0} key={index} />;
}